'use client'

import Script from 'next/script'

interface GoogleAnalyticsProps {
  measurementId: string
}

/**
 * Google Analytics 4 Client Component
 *
 * Add this to your app's root layout to enable GA4 tracking.
 *
 * @example
 * // app/layout.tsx
 * import { GoogleAnalytics } from '@vm/analytics/client'
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <GoogleAnalytics measurementId={process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID!} />
 *         {children}
 *       </body>
 *     </html>
 *   )
 * }
 */
export function GoogleAnalytics({ measurementId }: GoogleAnalyticsProps) {
  if (!measurementId) {
    return null
  }

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.__vmGaMeasurementId = '${measurementId}';
          gtag('js', new Date());
          // No page_path here: gtag reads the current document location for each
          // page_view. Pinning it freezes every later hit to the landing path,
          // so client-side navigations get misattributed to the entry page.
          gtag('config', '${measurementId}');
        `}
      </Script>
    </>
  )
}

/**
 * Track a custom event in GA4
 *
 * @example
 * trackEvent('button_click', { button_name: 'signup' })
 * trackEvent('purchase', { value: 9.99, currency: 'USD' })
 */
export function trackEvent(
  eventName: string,
  parameters?: Record<string, string | number | boolean>
) {
  if (typeof window !== 'undefined' && 'gtag' in window) {
    ;(window as unknown as { gtag: (...args: unknown[]) => void }).gtag(
      'event',
      eventName,
      parameters
    )
  }
}

/**
 * Track a page view (useful for SPA navigation)
 */
export function trackPageView(url: string) {
  if (typeof window !== 'undefined' && 'gtag' in window) {
    const measurementId = (window as unknown as { __vmGaMeasurementId?: string })
      .__vmGaMeasurementId
    if (!measurementId) return
    ;(window as unknown as { gtag: (...args: unknown[]) => void }).gtag(
      'event',
      'page_view',
      { page_location: new URL(url, window.location.origin).href }
    )
  }
}

/**
 * Track outbound link clicks (useful for affiliate links)
 *
 * @example
 * <a href={affiliateUrl} onClick={() => trackOutboundLink(affiliateUrl)}>
 *   Buy Domain
 * </a>
 */
export function trackOutboundLink(url: string, label?: string) {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: label || url,
    transport_type: 'beacon',
  })
}

/**
 * Track conversions (signups, purchases, etc.)
 */
export function trackConversion(conversionType: string, value?: number) {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value || 0,
  })
}
